import { AbsgArrayHelper, LogService } from '@absg/angular-common';
import { IStatusProvider } from '@absg/angular-components';
import { AuthenticationService } from '@absg/angular-platform';
import { RopcUserContract, UserClaims } from '@absg/api-identity';
import { UserContextValidation } from '@absg/api-party';
import { Constants } from '@absg/nim-client-shared';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { StationInfoService } from 'core.services/station-info.service';
import { PaginationInstance } from 'ngx-pagination';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { PaginationControlsComponent } from 'shared.root/components/pagination-controls/pagination-controls.component';
import { ItemPillExtendedViewHelper } from 'shared.root/helpers/item-pill-extendedview.helper';
import { ItemPillExtendedView } from 'shared.root/models/Item-pill-extendedview.model';
import { WitnessItem } from 'shared.root/models/witness-base.model';
import { WitnessService } from 'shared.root/services/witness.service';
import { SubSink } from 'subsink';

@Component({
  selector: '[nc-witness-login]',
  templateUrl: './witness-login.component.html',
  standalone: false,
})
export class WitnessLoginComponent implements OnInit, OnDestroy {
  @Input() isVerifyWitnessModalOpen = new BehaviorSubject<boolean>(false);
  @Input() selectedWitnessLoginItems = new BehaviorSubject<WitnessItem[]>([]);
  @Output() cancelVerifyWitnessModal = new EventEmitter<void>();
  @Output() verifyWitnessSuccess = new EventEmitter<UserClaims>();
  @ViewChild('txtUserName', { static: false }) txtUserName: ElementRef;

  verifyWitnessForm: UntypedFormGroup;
  verifyWitnessUserNameControl: UntypedFormControl;
  verifyWitnessPasswordControl: UntypedFormControl;
  private formControls = {
    verifyWitnessUserNameControl: '',
    verifyWitnessPasswordControl: '',
  };
  witnessLoginPagingConfig: PaginationInstance = PaginationControlsComponent.getDefaultConfig();
  private subsink = new SubSink();

  inputUserNameSameAsLoginUserName = false;
  popupSameUserNameValidationError = false;
  popupWitnessPermissionErrorMessage = false;
  popupInvalidUserError = false;
  isSpinnerVisible$ = new BehaviorSubject<boolean>(false);
  isSendingData = false;
  userNameErrorMessage = '';
  loginUserName = '';
  sameUserErrorMessage = 'Witness User must be different from Logged In User.';
  invalidUserErrorMessage = 'Please make sure the User Name and Password you provided are correct.';
  inActiveUser = 'Your account has been disabled. If you would like to enable your account, please contact your admin.';
  witnessPermissionErrorMessage = 'You do not have permission to be a witness.';
  gridWitnessLoginItems: WitnessItem[] = new Array<WitnessItem>();
  isPillModalOpen = new BehaviorSubject<boolean>(false);
  displayPillItem: ItemPillExtendedView = null;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private stationInfoService: StationInfoService,
    private loggingService: LogService,
    private authenticationService: AuthenticationService,
    private witnessService: WitnessService,
  ) {
  }

  ngOnInit(): void {
    this.createForm();
    this.setLoginUserName();
    this.witnessLoginPagingConfig = {
      id: 'witnessPagingConfig',
      totalItems: 0,
      currentPage: 1,
      itemsPerPage: 4,
    } as PaginationInstance;

    if (!AbsgArrayHelper.isNil(this.isVerifyWitnessModalOpen)) {
      this.subsink.sink = this.isVerifyWitnessModalOpen.subscribe((open) => {
        if (open) {
          this.witnessLoginPagingConfig.currentPage = 1;
          this.witnessLoginPagingConfig.id = this.setId();
        }
      });
    }
    if (!AbsgArrayHelper.isNil(this.selectedWitnessLoginItems)) {
      this.subsink.sink = this.selectedWitnessLoginItems.subscribe((items) => {
        if (!AbsgArrayHelper.isNil(items) && items.length > 0) {
          const list = this.witnessService.sortWitnessItemListByDisplayName(items);
          this.gridWitnessLoginItems = AbsgArrayHelper.uniqBy(list, (witItem) => witItem.positionContainerId);
        }
      });
    }
    this.witnessLoginPagingConfig.totalItems = this.gridWitnessLoginItems.length;
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  isVerifyWitnessButtonEnabled(): boolean {
    return this.verifyWitnessForm.valid && !this.popupSameUserNameValidationError && !this.inputUserNameSameAsLoginUserName
      && !this.isSendingData && !this.popupWitnessPermissionErrorMessage && !this.popupInvalidUserError;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBlur(event: any): void {
    const newValue = event.target.value;
    if (!AbsgArrayHelper.isNil(newValue) && String(newValue).trim().toUpperCase() === this.loginUserName) {
      this.userNameErrorMessage = this.sameUserErrorMessage;
      this.popupSameUserNameValidationError = true;
    }
  }

  setDefaultFocus(event: TransitionEvent = null): void {
    if (!AbsgArrayHelper.isNil(this.isVerifyWitnessModalOpen) && !this.isVerifyWitnessModalOpen.getValue()) {
      return;
    }
    if (!event || (event && event.propertyName === Constants.transitionEventPropertyName)) {
      this.txtUserName.nativeElement.focus();
    }
  }

  onCancelVerifyWitnessModal(event: Event): void {
    event.preventDefault();
    this.clearFieldsAndFlags();
    this.cancelVerifyWitnessModal.emit();
  }

  onVerifyWitnessButtonClicked(event: Event): void {
    event.preventDefault();
    this.popupInvalidUserError = false;
    this.userNameErrorMessage = '';
    this.verifyWitnessUser();
  }

  verifyWitnessUser(): void {
    this.setSpinnerVisible(true);
    this.setIsSendingData(true);
    this.witnessService.userClaims(this.createUserClaimsPayload()).pipe(take(1)).subscribe((response) => {
      if (!AbsgArrayHelper.isNil(response)) {
        this.onUserClaimsSuccess(response);
      }
    },
      () => {
        this.onVerifyWitnessFailure();
      },
    );
  }

  setSpinnerVisible(isVisible: boolean): void {
    this.isSpinnerVisible$.next(isVisible);
  }

  showAllPills(item: IStatusProvider): void {
    const pillItem = ItemPillExtendedViewHelper.convertWitnessItemToItemPillExtendedView(item as WitnessItem);
    this.displayPillItem = pillItem;
    this.isPillModalOpen.next(true);
  }

  closeModalEvent(isOpen: boolean): void {
    this.isPillModalOpen.next(isOpen);
  }

  setIsSendingData(isVisible: boolean): void {
    this.isSendingData = isVisible;
  }

  trackItem(__: number, item: WitnessItem): number {
    if (!item) { return undefined; }

    return item.inventoryItemId;
  }

  onVerifyWitnessPermissionFailure(): void {
    this.popupWitnessPermissionErrorMessage = true;
    this.userNameErrorMessage = this.witnessPermissionErrorMessage;
    this.setIsSendingData(false);
  }

  private onVerifyWitnessFailure(): void {
    this.popupInvalidUserError = true;
    if (this.witnessService.inActiveUser === true) {
      this.userNameErrorMessage = this.inActiveUser;
    } else {
      this.userNameErrorMessage = this.invalidUserErrorMessage;
    }
    this.setSpinnerVisible(false);
    this.setIsSendingData(false);
  }

  private onValidateUserContextSuccess(userClaimsResponse: UserClaims): void {
    this.clearFieldsAndFlags();
    this.verifyWitnessSuccess.emit(userClaimsResponse);
  }

  private onUserClaimsSuccess(userClaimsResponse: UserClaims): void {
    const userContextValidation = this.createUserContextValidationPayload(userClaimsResponse);
    if (!AbsgArrayHelper.isNil(userContextValidation)) {
      forkJoin([
        this.witnessService.validateUserContext(userContextValidation),
        this.witnessService.checkWitnessPermission(userContextValidation.userPartyId, userContextValidation.practicePartyId),
      ]).pipe(take(1))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .subscribe((results: any) => {
          if (!AbsgArrayHelper.isNil(results[0]) && results[0].IsValid) {
            if (results[1]) {
              this.onValidateUserContextSuccess(userClaimsResponse);
            } else {
              this.onVerifyWitnessPermissionFailure();
            }
          }
          this.setSpinnerVisible(false);
          this.setIsSendingData(false);
        },
          (error) => {
            this.onVerifyWitnessFailure();
            this.loggingService.logError(error);
          },
        );
    }
  }

  private createUserClaimsPayload(): RopcUserContract {
    return { userName: this.verifyWitnessUserNameControl.value, password: this.verifyWitnessPasswordControl.value } as RopcUserContract;
  }

  private createUserContextValidationPayload(userClaimsResponse: UserClaims): UserContextValidation {
    let userContextValidation: UserContextValidation = null;
    if (!AbsgArrayHelper.isNil(userClaimsResponse) && !AbsgArrayHelper.isNil(userClaimsResponse.userPartyId)) {
      this.authenticationService.getStationPracticeId()
        .pipe(take(1))
        .subscribe(practiceId => {
          if (!AbsgArrayHelper.isNil(practiceId)) {
            userContextValidation = {
              practicePartyId: practiceId,
              orgId: 1,
              userPartyId: Number(userClaimsResponse.userPartyId),
            };
          }
        });
    }
    return userContextValidation;
  }

  private setLoginUserName(): void {
    if (!AbsgArrayHelper.isNil(this.stationInfoService.getLoggedInUserName())) {
      this.loginUserName = this.stationInfoService.getLoggedInUserName().toUpperCase();
    }
  }

  private createForm(): void {
    this.verifyWitnessForm = this.formBuilder.group(this.formControls);
    this.verifyWitnessUserNameControl = this.verifyWitnessForm.controls['verifyWitnessUserNameControl'] as UntypedFormControl;
    this.verifyWitnessPasswordControl = this.verifyWitnessForm.controls['verifyWitnessPasswordControl'] as UntypedFormControl;
    this.verifyWitnessUserNameControl.setValidators(Validators.required);
    this.verifyWitnessPasswordControl.setValidators(Validators.required);
    this.subscribeToFormValueChanges();
  }

  private subscribeToFormValueChanges(): void {
    this.subsink.sink = this.verifyWitnessUserNameControl.valueChanges.pipe(distinctUntilChanged(), filter(value => value != null))
      .subscribe(value => {
        if (!AbsgArrayHelper.isNil(value) && String(value).toUpperCase() === this.loginUserName) {
          this.inputUserNameSameAsLoginUserName = true;
          this.userNameErrorMessage = this.sameUserErrorMessage;
        } else {
          this.inputUserNameSameAsLoginUserName = false;
          this.userNameErrorMessage = this.invalidUserErrorMessage;
          if (this.popupSameUserNameValidationError) {
            this.popupSameUserNameValidationError = false;
          }
          this.popupWitnessPermissionErrorMessage = false;
          this.popupInvalidUserError = false;
        }
      });

    this.subsink.sink = this.verifyWitnessPasswordControl.valueChanges.pipe(distinctUntilChanged(), filter(value => value != null))
      .subscribe(() => {
        this.inputUserNameSameAsLoginUserName = false;
        this.userNameErrorMessage = this.invalidUserErrorMessage;
        this.popupWitnessPermissionErrorMessage = false;
        this.popupInvalidUserError = false;
      });
  }

  private clearFieldsAndFlags(): void {
    this.verifyWitnessUserNameControl.setValue('');
    this.verifyWitnessPasswordControl.setValue('');
    this.popupSameUserNameValidationError = false;
    this.inputUserNameSameAsLoginUserName = false;
    this.popupWitnessPermissionErrorMessage = false;
    this.popupInvalidUserError = false;
    this.userNameErrorMessage = null;
  }

  private setId(): string {
    const name =
      this.gridWitnessLoginItems ? (this.gridWitnessLoginItems[0] ? (this.gridWitnessLoginItems[0].parentComponent) : 'name') : 'name';
    return `witnessPagingConfig${name}`;
  }
}
